/**
 * @file Modes squelettes activables : SpeedRun, Mine, Boss, Raid, Harvest (combat generique pilote par ModeRules) + Adventure, Fish, Hub (sans combat).
 */
import type { ModeId } from '@shared/enums';
import { HUB_CATNIP, HUB_GIANT_SPOTS, HUB_PLACES, HUB_SEASONS } from '@config/hub';
import type { ModeDef } from '@config/types';
import { stageMoney } from '../../balance/formulas';
import { spawnTarget } from '../../ecs/EntityFactory';
import { layoutPositions } from '../spawnLayout';
import type { SimContext } from '../../Context';
import type { With } from '../../ecs/World';
import { BaseMode } from '../BaseMode';
import type { ModeExitReason, ModeResult, ModeStartParams } from '../ModeTypes';

/**
 * Combat generique, 100% data-driven (ModeRules.spawn / timer / win / lose / killRewards) :
 *  - onStart : spawnFromRules(initialCount, palier).
 *  - respawn 'onKill' : respawn immediat d'une cible ; 'interval' : 1 cible toutes les respawnIntervalSeconds jusqu'a maxAlive.
 *  - fin : resultat construit par buildRewards() (a specialiser), applique via tx.grantBundle a l'entree en phase 'result'.
 * Le palier (stageValue utilise pour scaler PV/argent) vaut progress.bestStageValue sauf surcharge.
 */
export class GenericCombatMode<M extends ModeId = ModeId> extends BaseMode<M> {
  private respawnAcc = 0;
  /** Vrai si la partie est un balayage (sweep) : aucun combat, resultat immediat = record precedent. */
  protected sweeping = false;

  constructor(def: ModeDef<M>) {
    super(def);
  }

  /**
   * Niveaux sous la frontiere de la run : PV des cibles = PV d'un arbre de (frontiere - tierOffset). Les modes secondaires doivent
   * rester faisables (SpeedRun : des dizaines d'arbres en 45 s) alors qu'un arbre du Main a la frontiere demande plusieurs secondes.
   */
  protected tierOffset = 0;

  /**
   * Palier de difficulte du mode : base sur la frontiere de la RUN (run.bestStageValue, remise a zero par un Rebirth) et non sur le
   * record absolu : apres un Rebirth les upgrades sont repartis de 1, des cibles au niveau du record seraient quasi immortelles.
   */
  protected tierValue(ctx: SimContext): number {
    return Math.max(0, ctx.data.run.bestStageValue - this.tierOffset);
  }

  /** Palier utilise pour les RECOMPENSES (la frontiere de la run, sans decalage). */
  protected rewardTier(ctx: SimContext): number {
    return ctx.data.run.bestStageValue;
  }

  protected override onEnter(ctx: SimContext, _params: ModeStartParams): void {
    ctx.services.team.placeFormation(0, 0);
  }

  protected override onStart(ctx: SimContext): void {
    this.respawnAcc = 0;
    this.sweeping = false;
    const record = ctx.data.progress.modeBests[this.id] ?? 0;
    if (this.startParams.sweep && this.def.entry.sweep && record > 0) {
      // Balayage : rien n'apparait, le mode se termine en victoire avec le record comme score.
      this.sweeping = true;
      this.kills = record;
      this.bossKilled = true;
      if (this.timeLeft !== null) this.timeLeft = 0;
      return;
    }
    this.spawnInitial(ctx);
  }

  /** Population de depart du mode (par defaut : ModeRules.spawn.initialCount cibles). */
  protected spawnInitial(ctx: SimContext): void {
    this.spawnFromRules(ctx, this.def.rules.spawn.initialCount, this.tierValue(ctx));
  }

  /** Enregistre le record du mode (maximum). */
  protected record(ctx: SimContext, value: number): void {
    const bests = ctx.data.progress.modeBests;
    bests[this.id] = Math.max(bests[this.id] ?? 0, value);
  }

  protected override onRunning(ctx: SimContext, dt: number): void {
    const sp = this.def.rules.spawn;
    if (this.sweeping) return;
    if (sp.respawn !== 'interval' || !sp.respawnIntervalSeconds) return;
    this.respawnAcc += dt;
    while (this.respawnAcc >= sp.respawnIntervalSeconds) {
      this.respawnAcc -= sp.respawnIntervalSeconds;
      this.spawnFromRules(ctx, 1, this.tierValue(ctx));
    }
  }

  protected override onKill(ctx: SimContext, _target: With<'target' | 'health'>): void {
    if (this.sweeping) return;
    if (this.def.rules.spawn.respawn === 'onKill') this.spawnFromRules(ctx, 1, this.tierValue(ctx));
  }

  protected override onWin(ctx: SimContext): void {
    const rewards = this.buildRewards(ctx, true);
    this.lastResult = { mode: this.id, success: true, score: this.score, wave: this.wave, rewards };
    ctx.tx.grantBundle(this.lastResult.rewards, `mode:${this.id}`);
  }

  protected override onLose(ctx: SimContext): void {
    const rewards = this.buildRewards(ctx, false);
    this.lastResult = { mode: this.id, success: false, score: this.score, wave: this.wave, rewards };
    ctx.tx.grantBundle(this.lastResult.rewards, `mode:${this.id}`);
  }

  protected override onExit(ctx: SimContext, _reason: ModeExitReason): ModeResult | null {
    this.clearArena(ctx);
    return this.lastResult;
  }

  /** Recompenses de fin : a specialiser par mode (GAME_DESIGN.md §Modes secondaires). */
  protected buildRewards(_ctx: SimContext, _success: boolean): ModeResult['rewards'] {
    return {};
  }
}

/** SpeedRun : arbres en anneau, 45 s, argent x moneyMult ; balayage (sweep) = resultat du meilleur score sans combat. */
export class SpeedRunMode extends GenericCombatMode<'speedRun'> {
  /** Arbres "sprint" ~13x plus fragiles qu'un arbre de la frontiere (20 niveaux en dessous, PV x0,6). */
  protected override tierOffset = 12;

  protected override buildRewards(ctx: SimContext, _success: boolean): ModeResult['rewards'] {
    const money = stageMoney(ctx.config.balance, this.rewardTier(ctx)).times(this.kills * this.params.moneyMult);
    this.score = this.kills;
    this.record(ctx, this.kills);
    return { wallet: { money } };
  }
}

/** Mine : minerais places par bruit, 60 s, ressources + gemmes. Partage le systeme de cibles recoltables avec Harvest. */
export class MineMode extends GenericCombatMode<'mine'> {
  protected override tierOffset = 10;

  protected override onStart(ctx: SimContext): void {
    super.onStart(ctx);
    ctx.services.missions.increment('minePlays', 1);
  }

  protected override spawnInitial(ctx: SimContext): void {
    this.spawnFromRules(ctx, this.params.oreCount, this.tierValue(ctx));
  }

  protected override buildRewards(ctx: SimContext, _success: boolean): ModeResult['rewards'] {
    // Les ressources sont deja creditees a chaque coup fatal (killRewards = 'resources').
    this.score = this.kills;
    this.record(ctx, this.kills);
    return { wallet: { dia: this.kills * this.params.gemPerOre } };
  }
}

/** Recolte (futur craft) : bois/pierre/minerais, sans chrono, respawn periodique. Ressources creditees par killTarget ('resources'). */
export class HarvestMode extends GenericCombatMode<'harvest'> {}

/** Adventure : expeditions de familiers (pas de combat). La logique vit dans services.collectibles (startExpedition/claim). */
export class AdventureMode extends BaseMode<'adventure'> {
  protected override onResult(_ctx: SimContext): void {}
}

/** Aquarium : vitrine passive des poissons (pas de combat). */
export class FishMode extends BaseMode<'fish'> {
  protected override onResult(_ctx: SimContext): void {}
}

/**
 * Hub social : deplacement 100% manuel, AUCUN tir (ControlRules forceOff), decors non tirables (shootableOverride).
 * En ligne (rt.hub.online), l'herbe a chat vient du serveur (commandes hub.net.*) et les autres joueurs sont rendus hors simulation (app/HubSession) ; hors ligne l'herbe est locale.
 */
export class HubMode extends BaseMode<'hub'> {
  private catnipTimer = 0;

  protected override onEnter(ctx: SimContext, _params: ModeStartParams): void {
    // Saison / zoom / arme rangee remis a zero ; les chats arrivent du mode precedent (coordonnees du chemin) : on les replace sur la place.
    ctx.services.hub.reset();
    ctx.services.team.placeFormation(0, -8);
  }

  protected override onStart(ctx: SimContext): void {
    const season = HUB_SEASONS[ctx.rt.hub.season];
    // Lieux fixes (lacs, maisonnettes, fontaine), arbres geants de saison, puis le decor disperse autour.
    HUB_PLACES.forEach((place, i) => {
      const e = spawnTarget(ctx, ctx.index.target(place.targetId), place.x, place.y, { stageValue: 0 });
      // Les maisons tournent la porte vers la camera (+/- 0.5 rad, decalage deterministe par maison) : la porte est toujours visible.
      if (place.targetId === 'decor_house') e.transform.rot = -Math.PI / 2 + (((i * 37) % 11) - 5) * 0.1;
    });
    HUB_GIANT_SPOTS.forEach((spot, i) => {
      const id = season.giants[i % season.giants.length];
      if (id) spawnTarget(ctx, ctx.index.target(id), spot.x, spot.y, { stageValue: 0 });
    });
    this.spawnFromRules(ctx, this.def.rules.spawn.initialCount, 0, season.scatter);
    // en ligne, l'herbe a chat vient du serveur (hub.net.catnipAdd) : aucune brindille locale
    if (!ctx.rt.hub.online) for (let i = 0; i < HUB_CATNIP.initial; i++) this.spawnCatnip(ctx);
    this.catnipTimer = ctx.rng.spawn.range(HUB_CATNIP.intervalMin, HUB_CATNIP.intervalMax);
  }

  protected override onRunning(ctx: SimContext, dt: number): void {
    if (ctx.rt.hub.online) return;
    this.collectCatnip(ctx);
    this.catnipTimer -= dt;
    if (this.catnipTimer <= 0) {
      this.catnipTimer = ctx.rng.spawn.range(HUB_CATNIP.intervalMin, HUB_CATNIP.intervalMax);
      if (this.catnipEntities(ctx).length < HUB_CATNIP.max) this.spawnCatnip(ctx);
    }
  }

  private catnipEntities(ctx: SimContext): Array<With<'target'>> {
    return ctx.rt.world.query('target', 'target').filter((e) => e.target.defId === HUB_CATNIP.targetId);
  }

  /** Fait apparaitre une brindille a un endroit libre au hasard (hors lacs, maisons, arbres, chats). */
  private spawnCatnip(ctx: SimContext): void {
    const avoid = ctx.rt.catIds.map((id) => {
      const t = ctx.rt.world.get(id)?.transform;
      return { x: t?.x ?? 0, y: t?.y ?? 0, r: 3 };
    });
    for (const e of ctx.rt.world.ofKind('target')) avoid.push({ x: e.transform.x, y: e.transform.y, r: (e.collider?.radius ?? 1) + 0.4 });
    const [p] = layoutPositions(ctx.rng.spawn, { layout: 'scatter', arena: this.def.rules.arena, count: 1, minSpacing: 1.5, avoid });
    if (p) spawnTarget(ctx, ctx.index.target(HUB_CATNIP.targetId), p.x, p.y, { stageValue: 0 });
  }

  /** Une brindille est cueillie par le premier chat de l'equipe qui marche dessus. */
  private collectCatnip(ctx: SimContext): void {
    const cats = ctx.rt.catIds.map((id) => ctx.rt.world.get(id)?.transform).filter((t): t is NonNullable<typeof t> => t !== undefined);
    const r2 = HUB_CATNIP.collectRadius * HUB_CATNIP.collectRadius;
    for (const e of this.catnipEntities(ctx)) {
      const hit = cats.some((c) => (c.x - e.transform.x) ** 2 + (c.y - e.transform.y) ** 2 <= r2);
      if (!hit) continue;
      ctx.data.hub.catnip += 1;
      ctx.bus.emit('CatnipCollected', { x: e.transform.x, y: e.transform.y, total: ctx.data.hub.catnip });
      ctx.rt.world.destroy(e.id);
    }
  }

  protected override onResult(_ctx: SimContext): void {}
  protected override onExit(ctx: SimContext, _reason: ModeExitReason): ModeResult | null {
    ctx.services.hub.reset();
    ctx.services.hub.setNetOnline(false);
    this.clearArena(ctx);
    return null;
  }
}
